import { useMutation, useQuery } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import * as yup from 'yup';

import { useEffect } from 'react';
import Modal from '../../../../../components/modals/Modal';
import { Button } from '../../../../../components/common';
import { AvatarSelectInput, FormInput, FormTextArea, MultiSelectInput } from '../../../../../components/form';
import providerService from '../../../../../services/api/apps/appProviderService';
import appCategoryService from '../../../../../services/api/apps/appCategoryService';
import { stripBase64Prefix } from '../../../../../utils/strings';



function ModalAppProviderForm({ open, provider, onClose, onAdded, onUpdated }: Props) {
    const queryKey = ['payments', 'apps', 'categories', { pageSize: 50 }];
    //const queryClient = useQueryClient();
    const categoriesQuery = useQuery({
        queryKey: queryKey,
        queryFn: () => appCategoryService.getCategories({ pageSize: 50 }),
        staleTime: Infinity,
        retryDelay: Infinity,
        refetchOnWindowFocus: false, // Disable refetching on window focus
        refetchOnMount: false, // Disable refetching on component mount
        refetchInterval: false, // Disable interval-based refetching
    });


    useEffect(() => {
        if (open) {
            saveMutation.reset();
        }
    }, [open]);


    const saveMutation = useMutation({
        mutationFn: (data: any) => {
            return provider ? providerService.updateProvider(provider.id, data) : providerService.createProvider(data);
        }
    });


    const handleFormSubmit = async (formData: any, { resetForm }: any) => {
        const payload = {
            ...formData,
            base64logo: stripBase64Prefix(formData?.base64logo),
            appCategoryIds: formData?.appCategoryIds?.map((item: any) => item?.id),
        };

        saveMutation.mutate(payload, {
            onSuccess: (data) => {
                onClose();

                if (provider && onUpdated) {
                    onUpdated(data);
                }

                if (!provider && onAdded) {
                    onAdded(data);
                }
            },
            onError: () => {
            }
        });
    }



    return (
        <Modal open={open} onClose={false ? () => null : onClose} closeOnOutsideClicked={false}>
            <div className="inline-block w-[36rem] max-w-full">
                <div className="flex justify-between mb-5 px-10 pt-8">
                    <h2 className="font-medium text-xl">
                        {provider ? "Edit Provider" : "Add Provider"}
                    </h2>

                    <button type="button" onClick={onClose} className="text-2xl text-gray-500 [&:not(:disabled)]:hover:text-gray-600 dark:text-gray-600 dark:[&:not(:disabled)]:hover:text-gray-500 focus:outline-none" disabled={saveMutation.isPending}>
                        <i className="ri-close-line"></i>
                    </button>
                </div>

                <div className='max-h-[80vh] overflow-y-auto px-10 pb-6'>
                    <Formik
                        initialValues={{
                            name: provider?.name || '',
                            description: provider?.description || '',
                            appCategoryIds: provider ? provider.appCategoryIds : [],
                            webAddress: provider?.webAddress || '',
                            base64logo: provider?.base64logo || '',
                            //status: provider?.status || 'ACTIVE',
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({ values, errors, touched, setErrors, setFieldValue, setFieldTouched, submitCount, isValid }) => (
                            <Form className="">
                                {
                                    saveMutation.error &&
                                    <p className='text-red-500 mb-4'>{saveMutation.error as unknown as string}</p>
                                }


                                <div className="mb-8">
                                    <label htmlFor="input-name" className='block text-sm mb-2'>Provider Name</label>
                                    <FormInput type="text" name="name" id="input-name" className="w-full" placeholder="Enter provider name" />
                                    {touched['name'] && errors['name'] && <p className='text-sm text-red-500 mt-2'>{errors['name'] as string}</p>}
                                </div>

                                <div className="mb-8">
                                    <label htmlFor="input-description" className='block text-sm mb-2'>Description</label>
                                    <FormTextArea name='description' id="input-description" className='w-full' placeholder="Type here" rows={3} />
                                    {touched['description'] && errors['description'] && <p className='text-sm text-red-500 mt-2'>Description is Required</p>}
                                </div>

                                <div className="mb-10">
                                    <label htmlFor="input-user-type" className='block text-sm mb-2'>Add Special Permissions</label>
                                    <MultiSelectInput
                                        items={categoriesQuery.data?.data}
                                        valueKey='id'
                                        labelKey='name'
                                        placeholder="Select category"
                                        selectedLabel='Category'
                                        selectedLabelPlural='Categories'
                                        showSelectedItems={false}
                                        value={values["appCategoryIds"]}
                                        onChange={(selected: any[]) => setFieldValue("appCategoryIds", selected)}
                                        usePortal
                                    />
                                    {touched['appCategoryIds'] && errors['appCategoryIds'] && <p className='text-sm text-red-500 mt-2'>{errors['appCategoryIds'] as string}</p>}
                                </div>

                                <div className="mb-8">
                                    <label htmlFor="input-web-address" className='block text-sm mb-2'>Web Address</label>
                                    <FormInput type="text" name="webAddress" id="input-web-address" className="w-full" placeholder="Enter web address" />
                                    {touched['webAddress'] && errors['webAddress'] && <p className='text-sm text-red-500 mt-2'>{errors['webAddress'] as string}</p>}
                                </div>

                                {/*  <div className="mb-8">
                                    <label htmlFor="input-logo" className='block text-sm mb-2'>Base 64 Logo</label>
                                    <FormTextArea name='base64logo' id="input-logo" className='w-full' placeholder="Paste..." rows={3} />
                                    {touched['base64logo'] && errors['base64logo'] && <p className='text-sm text-red-500 mt-2'>{errors['base64logo'] as string}</p>}
                                </div> */}

                                <div className='mb-6'>
                                    <AvatarSelectInput
                                        size={6.2}
                                        value={values['base64logo']}
                                        placeholder='Select Logo'
                                        label='Select Logo'
                                        layout='inline'
                                        fileSize={0.8}
                                        onChange={(value) => setFieldValue('base64logo', value)}
                                    />
                                    {touched['base64logo'] && errors['base64logo'] && <p className='text-sm text-red-500 mt-2'>{errors['base64logo'] as string}</p>}
                                </div>


                                {/* <div className='mb-10'>
                                    <label htmlFor="" className='block mb-2'>Status</label>
                                    <RadioGroupInput
                                        value={values['status']}
                                        options={[
                                            { label: "Active", value: 'ACTIVE' },
                                            { label: "Inactive", value: 'INACTIVE' },
                                        ]}
                                        onChange={(value) => setFieldValue('status', value)}
                                    />
                                </div> */}


                                <div className='flex justify-end items-center'>
                                    <Button type='button' onClick={onClose} className='!text-gray-500 dark:!text-white bg-gray-100 hocus:bg-gray-200  dark:bg-gray-800 dark:hocus:bg-gray-600 py-2.5 mr-6'>
                                        Cancel
                                    </Button>

                                    <Button type='submit' loading={saveMutation.isPending} className='' disabled={saveMutation.isPending}>
                                        {provider ? "Save" : "Create Category"}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </Modal>
    );
}


interface Props {
    open: boolean;
    provider: any;
    onClose: () => void;
    onAdded?: (data: any) => void;
    onUpdated?: (data: any) => void;
}


const validationSchema = yup.object().shape({
    //name: yup.string().trim().matches(/^[^!@#$%^&*()]+$/, 'No special characters are allowed').required('Category name is required'),
    // description: yup.string().trim().required(),
    //status: yup.string().trim().required(),

    name: yup
        .string()
        .required("Name is required")
        .min(2, "Name must be at least 2 characters"),

    base64logo: yup
        .string()
        .required("Logo is required"),
    /*  .matches(
         /^data:image\/(png|jpg|jpeg|gif|svg\+xml);base64,[A-Za-z0-9+/=]+$/,
         "Logo must be a valid base64 image string"
     ), */

    webAddress: yup
        .string()
        .required("Web address is required")
        .url("Must be a valid URL"),

    description: yup
        .string()
        .required("Description is required"),
    //.min(10, "Description must be at least 10 characters"),

    appCategoryIds: yup
        .array()
        //.of(yup.number().integer().positive("Must be a positive number"))
        .required("At least one category is required")
        .min(1, "Select at least one category"),
});


export default ModalAppProviderForm;